import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Rewritten from the tokens (§5 Shell), using ui-sprocket src/components/layout/AppLayout.tsx as
// the reference for the shape only — reviewed 2026-09-21: the retired layout was bound to the
// old tenant/auth hooks, nav catalogue and `/{slug}` routes; this one takes everything as props
// and a router adapter, so both shells (and the playground) mount it unchanged.
import { forwardRef, useEffect, useId, useState } from 'react';
import Avatar from '@mui/material/Avatar';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import MuiIconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { LogoutIcon, MenuIcon, MoreHorizIcon, SearchIcon, SwapHorizIcon } from '../../icons.js';
import { PHONE_BREAKPOINT } from '../../tokens/breakpoints.js';
import { MOTION } from '../../tokens/motion.js';
import { RADIUS } from '../../tokens/radius.js';
import { SIZE } from '../../tokens/space.js';
import { rem, TYPE } from '../../tokens/type.js';
import { BrandMark } from '../brand-mark.js';
import { IconButton } from '../button.js';
import { ColorModeToggle } from '../color-mode-toggle.js';
import { initialsFor } from '../name-avatar.js';
import { MAIN_CONTENT_ID, SIDEBAR_WIDTH_VAR } from './constants.js';
const Anchor = forwardRef(function Anchor({ href, children, ...rest }, ref) {
    return (_jsx("a", { ref: ref, href: href, ...rest, children: children }));
});
/**
 * The app shell (§5). No header band: the permanent 240px sidebar carries the workspace
 * row, search, grouped navigation and the account row; each page owns its own header. On
 * phones a 48px top row (mark, search, avatar) stands in for the sidebar, with a bottom
 * tab bar for the core destinations and the full nav behind "More".
 */
export function AppShell({ brand, workspace, search, nav, account, bottomTabs, LinkComponent = Anchor, appearance = true, children, }) {
    const theme = useTheme();
    const isPhone = useMediaQuery(theme.breakpoints.down(PHONE_BREAKPOINT));
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const menuId = useId();
    // Let portaled surfaces (toasts) clear the sidebar without knowing about it.
    useEffect(() => {
        document.documentElement.style.setProperty(SIDEBAR_WIDTH_VAR, isPhone ? '0px' : `${SIZE.sidebarWidth}px`);
        return () => {
            document.documentElement.style.removeProperty(SIDEBAR_WIDTH_VAR);
        };
    }, [isPhone]);
    const openMenu = (e) => setMenuAnchor(e.currentTarget);
    const closeMenu = () => setMenuAnchor(null);
    const closeDrawer = () => setDrawerOpen(false);
    const initials = initialsFor(account.name);
    const allItems = nav.flatMap((g) => g.items);
    const tabs = bottomTabs ?? allItems.slice(0, 4);
    const activeTab = tabs.find((t) => t.active)?.id ?? 'more';
    // A console with no bottom tab bar (`bottomTabs={[]}`) gets a menu button in the phone top row.
    const hasTabBar = tabs.length > 0;
    const accountMenu = (_jsxs(Menu, { id: menuId, anchorEl: menuAnchor, open: Boolean(menuAnchor), onClose: closeMenu, anchorOrigin: {
            vertical: isPhone ? 'bottom' : 'top',
            horizontal: isPhone ? 'right' : 'left',
        }, transformOrigin: {
            vertical: isPhone ? 'top' : 'bottom',
            horizontal: isPhone ? 'right' : 'left',
        }, slotProps: { paper: { sx: { minWidth: 240 } }, list: { 'aria-label': 'Account' } }, children: [_jsx(MenuItem, { disabled: true, sx: { opacity: '1 !important' }, children: _jsx(ListItemText, { primary: account.name, secondary: account.email, slotProps: {
                        primary: { sx: { fontWeight: 500 } },
                        secondary: { sx: { fontSize: rem(TYPE.caption.size) } },
                    } }) }), _jsx(Divider, {}), account.items.map((item) => item.href ? (_jsxs(MenuItem, { component: LinkComponent, href: item.href, selected: item.active, onClick: () => {
                    closeMenu();
                    closeDrawer();
                    item.onSelect?.();
                }, children: [item.icon && (_jsx(ListItemIcon, { sx: { '& svg': { fontSize: 18 } }, children: item.icon })), item.label] }, item.id)) : (_jsxs(MenuItem, { selected: item.active, onClick: () => {
                    closeMenu();
                    closeDrawer();
                    item.onSelect?.();
                }, children: [item.icon && (_jsx(ListItemIcon, { sx: { '& svg': { fontSize: 18 } }, children: item.icon })), item.label] }, item.id))), account.items.length > 0 && _jsx(Divider, {}), appearance && _jsx(ColorModeToggle, {}), appearance && _jsx(Divider, {}), _jsxs(MenuItem, { onClick: () => {
                    closeMenu();
                    account.onSignOut();
                }, children: [_jsx(ListItemIcon, { children: _jsx(LogoutIcon, { fontSize: "small" }) }), account.signOutLabel ?? 'Sign out'] })] }));
    const sidebar = (_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', height: '100%', p: 1.5, gap: 0 }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.5 }, children: [_jsx(Box, { component: LinkComponent, href: brand.homeHref, onClick: closeDrawer, "aria-label": `${brand.name} home`, sx: {
                            display: 'flex',
                            alignItems: 'center',
                            height: 36,
                            px: 1,
                            minWidth: 0,
                            flexGrow: 1,
                            textDecoration: 'none',
                            color: 'text.primary',
                            borderRadius: `${RADIUS.md}px`,
                        }, children: _jsx(BrandMark, { name: brand.name, size: "sm" }) }), workspace && (_jsx(Tooltip, { title: workspace.switchLabel ?? 'Switch workspace', children: _jsx(MuiIconButton, { "aria-label": workspace.switchLabel ?? 'Switch workspace', size: "small", component: LinkComponent, href: workspace.switchHref, onClick: closeDrawer, children: _jsx(SwapHorizIcon, { sx: { fontSize: 18 } }) }) }))] }), workspace?.meta && (_jsx(Typography, { variant: "caption", color: "text.tertiary", sx: { px: 1, mt: -0.25, display: 'block' }, children: workspace.meta })), search && (_jsxs(ButtonBase, { onClick: () => {
                    closeDrawer();
                    search.onOpen();
                }, "aria-label": search.label ?? 'Search', "aria-keyshortcuts": "Control+K Meta+K", sx: (t) => ({
                    mt: 1.5,
                    mb: 1,
                    height: SIZE.control.medium,
                    px: 1.25,
                    gap: 1,
                    justifyContent: 'flex-start',
                    borderRadius: `${RADIUS.sm}px`,
                    border: `1px solid ${t.palette.border}`,
                    bgcolor: 'background.paper',
                    color: 'text.tertiary',
                    fontSize: rem(TYPE.bodySmall.size),
                    boxShadow: t.customShadows.card,
                    transition: t.transitions.create(['border-color', 'box-shadow'], {
                        duration: MOTION.duration.base,
                    }),
                    '&:hover': { borderColor: t.palette.text.tertiary },
                }), children: [_jsx(SearchIcon, { sx: { fontSize: 16 } }), _jsx(Box, { component: "span", sx: { flexGrow: 1, textAlign: 'left' }, children: search.label ?? 'Search or jump to…' }), _jsx(Box, { component: "kbd", "aria-hidden": "true", sx: (t) => ({
                            display: { xs: 'none', md: 'inline' },
                            px: 0.5,
                            fontFamily: 'inherit',
                            fontSize: rem(11.5),
                            lineHeight: '18px',
                            border: `1px solid ${t.palette.hairline}`,
                            borderRadius: '4px',
                            bgcolor: 'background.subtle',
                        }), children: search.hint ?? '⌘K' })] })), _jsx(Box, { component: "nav", "aria-label": "Main", sx: {
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.25,
                    overflowY: 'auto',
                    minHeight: 0,
                    mt: search ? 0 : 2,
                }, children: nav.map((group) => (_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 0.25 }, children: [group.label && (_jsx(Typography, { variant: "overline", component: "div", sx: { color: 'text.tertiary', px: 1.25, pt: 2, pb: 0.5 }, children: group.label })), group.items.map((item) => (_jsx(SidebarLink, { item: item, LinkComponent: LinkComponent, onNavigate: closeDrawer }, item.id)))] }, group.id))) }), _jsxs(ButtonBase, { onClick: openMenu, "aria-label": "Account menu", "aria-haspopup": "menu", "aria-expanded": Boolean(menuAnchor), "aria-controls": menuAnchor ? menuId : undefined, sx: (t) => ({
                    mt: 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    p: 1,
                    borderRadius: `${RADIUS.md}px`,
                    textAlign: 'left',
                    '&:hover': { bgcolor: t.palette.action.hover },
                }), children: [_jsx(Avatar, { sx: { width: 28, height: 28, bgcolor: 'brand.tint', color: 'brand.text' }, children: initials }), _jsxs(Box, { sx: { minWidth: 0, flexGrow: 1 }, children: [_jsx(Typography, { variant: "subtitle2", noWrap: true, sx: { lineHeight: 1.2 }, children: account.name }), _jsx(Typography, { variant: "caption", color: "text.tertiary", noWrap: true, sx: { display: 'block', lineHeight: 1.3 }, children: account.email })] }), _jsx(MoreHorizIcon, { sx: { fontSize: 18, color: 'text.tertiary' } })] })] }));
    return (_jsxs(Box, { sx: { display: 'flex', minHeight: '100vh', bgcolor: 'background.canvas' }, children: [_jsx(Box, { component: "a", href: `#${MAIN_CONTENT_ID}`, sx: {
                    position: 'fixed',
                    top: 8,
                    left: 8,
                    zIndex: (t) => t.zIndex.modal + 1,
                    px: 2,
                    py: 1,
                    borderRadius: `${RADIUS.sm}px`,
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                    border: '1px solid',
                    borderColor: 'hairline',
                    textDecoration: 'none',
                    fontWeight: 600,
                    transform: 'translateY(-150%)',
                    transition: `transform ${MOTION.duration.fast}ms ${MOTION.easing}`,
                    '&:focus-visible': { transform: 'translateY(0)' },
                }, children: "Skip to content" }), isPhone ? (_jsx(Drawer, { variant: "temporary", open: drawerOpen, onClose: closeDrawer, slotProps: { root: { keepMounted: true } }, sx: {
                    '& .MuiDrawer-paper': {
                        width: SIZE.sidebarWidth,
                        boxSizing: 'border-box',
                        bgcolor: 'background.sidebar',
                    },
                }, children: sidebar })) : (_jsx(Drawer, { variant: "permanent", sx: {
                    width: SIZE.sidebarWidth,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: SIZE.sidebarWidth,
                        boxSizing: 'border-box',
                        bgcolor: 'background.sidebar',
                        borderRight: '1px solid',
                        borderColor: 'hairline',
                    },
                }, children: sidebar })), _jsxs(Box, { sx: { flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }, children: [isPhone && (_jsxs(Box, { component: "header", sx: {
                            px: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            borderBottom: '1px solid',
                            borderColor: 'hairline',
                            bgcolor: 'background.paper',
                            // An installed app fills the screen including the status bar; the inset
                            // grows the box rather than squashing the row.
                            pt: 'env(safe-area-inset-top)',
                            height: `calc(${SIZE.mobileBarHeight}px + env(safe-area-inset-top))`,
                        }, children: [_jsx(Box, { component: LinkComponent, href: brand.homeHref, "aria-label": `${brand.name} home`, sx: { textDecoration: 'none', color: 'text.primary', flexGrow: 1, minWidth: 0 }, children: _jsx(BrandMark, { name: brand.name, size: "sm" }) }), !hasTabBar && (_jsx(IconButton, { onClick: () => setDrawerOpen(true), label: "Menu", size: "small", tooltip: false, "aria-haspopup": "dialog", "aria-expanded": drawerOpen, children: _jsx(MenuIcon, {}) })), search && (_jsx(IconButton, { onClick: search.onOpen, label: search.label ?? 'Search', size: "small", tooltip: false, children: _jsx(SearchIcon, {}) })), _jsx(IconButton, { onClick: openMenu, size: "small", label: "Account menu", tooltip: false, "aria-haspopup": "menu", "aria-expanded": Boolean(menuAnchor), "aria-controls": menuAnchor ? menuId : undefined, children: _jsx(Avatar, { sx: { width: 28, height: 28, bgcolor: 'brand.tint', color: 'brand.text' }, children: initials }) })] })), _jsx(Box, { component: "main", id: MAIN_CONTENT_ID, tabIndex: -1, sx: {
                            flexGrow: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            outline: 'none',
                            px: { xs: 2, sm: 3, md: 4 },
                            pt: { xs: 2, md: 3 },
                            pb: { xs: hasTabBar ? 10 : 4, md: 4 },
                        }, children: _jsx(Box, { sx: {
                                width: '100%',
                                maxWidth: SIZE.contentMaxWidth,
                                mx: 'auto',
                                flexGrow: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                minWidth: 0,
                            }, children: children }) })] }), accountMenu, isPhone && hasTabBar && (_jsx(Paper, { component: "nav", "aria-label": "Primary", elevation: 0, sx: {
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: (t) => t.zIndex.appBar,
                    borderTop: '1px solid',
                    borderColor: 'hairline',
                    // Clear the home indicator on a notched phone (needs viewport-fit=cover).
                    pb: 'env(safe-area-inset-bottom)',
                }, children: _jsxs(BottomNavigation, { value: activeTab, showLabels: true, children: [tabs.map((item) => (_jsx(BottomNavigationAction, { value: item.id, label: item.label, icon: item.icon, component: LinkComponent, href: item.href, ...(item.active ? { 'aria-current': 'page' } : {}) }, item.id))), _jsx(BottomNavigationAction, { value: "more", label: "More", icon: _jsx(MenuIcon, {}), onClick: () => setDrawerOpen(true), "aria-haspopup": "dialog" })] }) }))] }));
}
function SidebarLink({ item, LinkComponent, onNavigate, }) {
    return (_jsxs(Box, { component: LinkComponent, href: item.href, onClick: onNavigate, ...(item.active ? { 'aria-current': 'page' } : {}), className: item.active ? 'active' : undefined, sx: (t) => ({
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            height: SIZE.navRow,
            px: 1.25,
            borderRadius: `${RADIUS.sm}px`,
            color: 'text.primary',
            textDecoration: 'none',
            fontSize: rem(TYPE.label.size),
            fontWeight: 500,
            lineHeight: 1,
            transition: t.transitions.create(['background-color', 'color'], {
                duration: MOTION.duration.fast,
            }),
            '& svg': {
                fontSize: 18,
                color: t.palette.text.secondary,
                transition: `color ${MOTION.duration.fast}ms`,
            },
            '&:hover': { bgcolor: t.palette.action.hover },
            '&.active': { bgcolor: t.palette.brand.tint, color: t.palette.brand.text },
            '&.active svg': { color: t.palette.brand.text },
            '&:focus-visible': {
                outline: `2px solid ${t.palette.primary.main}`,
                outlineOffset: 2,
            },
        }), children: [item.icon, _jsx(Box, { component: "span", sx: { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }, children: item.label })] }));
}
//# sourceMappingURL=app-shell.js.map